import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * "Edit booking" for a tour line in the cart drawer. Renders as a slide-in panel
 * INSIDE the cart drawer dialog (so it shares the drawer's top layer rather than
 * sitting behind it) and mirrors the PDP booking drawer: date → time → guests →
 * lunch. On save it adds a new line for the chosen slot and removes the old one.
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYNAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const pad = (n) => (n < 10 ? '0' : '') + n;
const fmtTime = (t) => { const h = parseInt(t.split(':')[0], 10), m = t.split(':')[1]; const ap = h >= 12 ? 'pm' : 'am'; const h12 = ((h + 11) % 12) + 1; return h12 + (m === '00' ? '' : ':' + m) + ap; };
const fmtDate = (s) => { const dt = new Date(s + 'T00:00:00'); return DAYS_SHORT[dt.getDay()] + ' ' + dt.getDate() + ' ' + MONTHS[dt.getMonth()].slice(0, 3); };
const money = (v, fmt) => (fmt || '£{{amount}}').replace(/\{\{\s*amount\s*\}\}/, v.toFixed(2)).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(v));

function parseLunch(str) {
  const out = {};
  if (!str) return out;
  String(str).split(',').forEach((part) => {
    const m = part.trim().match(/^(\d+)\s*[x×]\s*(.+)$/i);
    if (m) out[m[2].trim()] = parseInt(m[1], 10);
  });
  return out;
}

class CartTourEdit {
  constructor(panel) {
    this.panel = panel;
    const q = (s) => panel.querySelector(s);
    this.elCal = q('[data-cte-cal]');
    this.elTimes = q('[data-cte-times]');
    this.elTimeWrap = q('[data-cte-timewrap]');
    this.elGuestWrap = q('[data-cte-guestwrap]');
    this.elPeople = q('[data-cte-people]');
    this.elMaxHint = q('[data-cte-maxhint]');
    this.elLunchWrap = q('[data-cte-lunchwrap]');
    this.elBoards = q('[data-cte-boards]');
    this.elLunchSub = q('[data-cte-lunchsub]');
    this.elTitle = q('[data-cte-title]');
    this.elTotal = q('[data-cte-total]');
    this.elSave = q('[data-cte-save]');
    this.elEmpty = q('[data-cte-empty]');
    this.elBody = q('.cte__body');

    // Keep every click inside the panel from bubbling to the cart drawer dialog's
    // outside-click handler — re-rendering the calendar detaches the clicked node,
    // which the dialog would otherwise read as a click outside and close itself.
    panel.addEventListener('click', (e) => e.stopPropagation());

    panel.querySelectorAll('[data-cte-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    q('[data-cte-dec]').addEventListener('click', () => { if (this.people > 1) { this.people--; this.afterGuestChange(); } });
    q('[data-cte-inc]').addEventListener('click', () => { if (this.people < this.cap()) { this.people++; this.afterGuestChange(); } });
    this.elSave.addEventListener('click', () => this.save());
  }

  cap() { return (this.selDate && this.selTime) ? this.availOf(this.selDate + ' ' + this.selTime) : (this.data ? this.data.capacity : 8); }

  open(data) {
    this.data = data;
    this.selDate = null;
    this.selTime = null;
    this.people = data.guests || 1;
    // Included lunch is stored as a single property on the tour line; paid
    // add-on lunch is stored as separate (charged) board lines, surfaced here as
    // `addons`. Either way boardQty is keyed by board name.
    if (data.lunchIncluded) {
      this.boardQty = parseLunch(data.lunchProp);
    } else {
      this.boardQty = {};
      (data.addons || []).forEach((a) => { this.boardQty[a.name] = a.qty; });
    }
    this.elTitle.textContent = data.title || 'your tour';
    this.elPeople.textContent = this.people;
    this.elTimeWrap.hidden = true;
    this.elGuestWrap.hidden = true;
    this.elLunchWrap.hidden = true;
    this.elSave.disabled = true;
    this.boardsBuilt = false;
    if (this.elBody) this.elBody.scrollTop = 0;

    const slotMap = {};
    (data.slots || []).forEach((s) => { if (s.time) slotMap[s.time] = s.id; });
    this.slotMap = slotMap;
    const overrides = data.capacityOverrides || {}, counts = data.counts || {};
    // Exclusive (private-hire) tours: any booked slot is closed outright.
    // Counts only include placed orders, so the customer's own cart line never
    // blocks them from keeping their current slot.
    this.availOf = (k) => {
      if (data.exclusive && (counts[k] || 0) > 0) return 0;
      return (overrides[k] != null ? overrides[k] : data.capacity) - (counts[k] || 0);
    };

    const blackouts = (data.blackouts || []).map((d) => String(d).slice(0, 10));
    const now = new Date(), today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(); end.setDate(end.getDate() + (data.windowWeeks || 8) * 7);
    if (data.availableUntil) { const au = new Date(data.availableUntil + 'T23:59:59'); if (!isNaN(au) && au < end) end.setTime(au.getTime()); }
    const byDate = {};
    for (let d = new Date(today0); d <= end; d.setDate(d.getDate() + 1)) {
      const ds = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
      if (blackouts.indexOf(ds) !== -1) continue;
      const weekday = DAYNAMES[d.getDay()], mmdd = ds.slice(5), times = {};
      if (data.seasonFrom && data.seasonTo && (mmdd < data.seasonFrom || mmdd > data.seasonTo)) continue;
      if ((data.baseDays || []).indexOf(weekday) !== -1) (data.baseTimes || []).forEach((t) => { times[t] = 1; });
      if (data.summerFrom && data.summerTo && mmdd >= data.summerFrom && mmdd <= data.summerTo && (data.summerDays || []).indexOf(weekday) !== -1) (data.summerTimes || []).forEach((t) => { times[t] = 1; });
      const list = Object.keys(times).filter((t) => slotMap[t] && new Date(ds + 'T' + t + ':00') > now).sort();
      if (list.length) byDate[ds] = list;
    }
    this.byDate = byDate;
    this.openDates = Object.keys(byDate).filter((ds) => byDate[ds].some((t) => this.availOf(ds + ' ' + t) > 0)).sort();

    this.calMonth = new Date((this.openDates[0] || (today0.getFullYear() + '-' + pad(today0.getMonth() + 1) + '-' + pad(today0.getDate()))) + 'T00:00:00');
    this.calMonth = new Date(this.calMonth.getFullYear(), this.calMonth.getMonth(), 1);
    this.lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    this.firstMonth = new Date(today0.getFullYear(), today0.getMonth(), 1);
    if (this.elEmpty) this.elEmpty.hidden = this.openDates.length > 0;
    this.renderCal();
    this.update();

    this.panel.removeAttribute('hidden');
    void this.panel.offsetWidth;
    this.panel.classList.add('is-open');
    this.panel.style.transform = 'translateX(0)';
  }

  renderCal() {
    const y = this.calMonth.getFullYear(), mo = this.calMonth.getMonth();
    const first = new Date(y, mo, 1), startDow = (first.getDay() + 6) % 7, days = new Date(y, mo + 1, 0).getDate();
    const prevOk = new Date(y, mo, 1) > this.firstMonth, nextOk = new Date(y, mo, 1) < this.lastMonth;
    let html = '<div class="cte__cal-head"><button type="button" class="cte__cal-nav" data-cte-prev ' + (prevOk ? '' : 'disabled') + ' aria-label="Previous month">&#8249;</button>' +
      '<span class="cte__cal-title">' + MONTHS[mo] + ' ' + y + '</span>' +
      '<button type="button" class="cte__cal-nav" data-cte-next ' + (nextOk ? '' : 'disabled') + ' aria-label="Next month">&#8250;</button></div>';
    html += '<div class="cte__cal-grid">' + DOW.map((d) => '<span class="cte__cal-dow">' + d + '</span>').join('');
    for (let i = 0; i < startDow; i++) html += '<span class="cte__cal-day is-empty"></span>';
    for (let dn = 1; dn <= days; dn++) {
      const ds = y + '-' + pad(mo + 1) + '-' + pad(dn);
      const open = (this.byDate[ds] || []).some((t) => this.availOf(ds + ' ' + t) > 0);
      const cls = 'cte__cal-day' + (open ? ' is-open' : '') + (ds === this.selDate ? ' is-active' : '');
      html += '<button type="button" class="' + cls + '" data-cte-day="' + ds + '"' + (open ? '' : ' disabled') + '>' + dn + '</button>';
    }
    html += '</div>';
    this.elCal.innerHTML = html;
    const prev = this.elCal.querySelector('[data-cte-prev]'), next = this.elCal.querySelector('[data-cte-next]');
    if (prev) prev.addEventListener('click', () => { this.calMonth = new Date(y, mo - 1, 1); this.renderCal(); });
    if (next) next.addEventListener('click', () => { this.calMonth = new Date(y, mo + 1, 1); this.renderCal(); });
    this.elCal.querySelectorAll('[data-cte-day]:not([disabled])').forEach((b) => b.addEventListener('click', () => this.selectDate(b.getAttribute('data-cte-day'))));
  }

  selectDate(ds) {
    this.selDate = ds;
    this.selTime = null;
    this.renderCal();
    this.renderTimes();
    this.update();
  }

  renderTimes() {
    const times = this.byDate[this.selDate] || [];
    this.elTimeWrap.hidden = false;
    this.elTimes.innerHTML = '';
    times.forEach((t) => {
      const avail = this.availOf(this.selDate + ' ' + t);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cte__chip' + (t === this.selTime ? ' is-active' : '');
      b.innerHTML = fmtTime(t) + (avail > 0 ? ' <small>' + avail + ' left</small>' : ' <small>full</small>');
      if (avail <= 0) b.disabled = true;
      b.addEventListener('click', () => {
        this.selTime = t;
        if (this.people > avail) this.people = avail;
        this.elPeople.textContent = this.people;
        this.renderTimes();
        this.update();
        this.scrollToGuests();
      });
      this.elTimes.appendChild(b);
    });
  }

  // Once a time is picked, scroll section 3 ("guests") to the top of the panel
  // body so it's obvious there are further steps below the fold.
  scrollToGuests() {
    if (!this.elBody || this.elGuestWrap.hidden) return;
    requestAnimationFrame(() => {
      const top = this.elGuestWrap.offsetTop - this.elBody.offsetTop;
      if (this.elBody.scrollTo) this.elBody.scrollTo({ top, behavior: 'smooth' });
      else this.elBody.scrollTop = top;
    });
  }

  afterGuestChange() {
    this.elPeople.textContent = this.people;
    this.renderLunch();
    this.update();
  }

  totalBoards() { return Object.keys(this.boardQty).reduce((s, k) => s + (this.boardQty[k] || 0), 0); }

  // Paid add-on lunch only: sum of selected board prices. Included lunch is free.
  boardsTotal() {
    const d = this.data;
    if (d.lunchIncluded) return 0;
    return (d.boards || []).reduce((s, bd) => s + (this.boardQty[bd.name] || 0) * (bd.price || 0), 0);
  }

  renderLunch() {
    const d = this.data;
    if (!d.hasLunch || !(d.boards || []).length) { this.elLunchWrap.hidden = true; return; }
    this.elLunchWrap.hidden = !(this.selDate && this.selTime);
    // Included lunch is one board per guest, so trim any selection above the
    // current guest count. Paid add-ons aren't capped to the guest count.
    if (d.lunchIncluded) {
      while (this.totalBoards() > this.people) {
        const k = Object.keys(this.boardQty).find((x) => this.boardQty[x] > 0);
        if (!k) break;
        this.boardQty[k]--;
      }
    }
    this.elBoards.innerHTML = '';
    d.boards.forEach((bd) => {
      if (this.boardQty[bd.name] == null) this.boardQty[bd.name] = 0;
      const row = document.createElement('div'); row.className = 'cte__board';
      const img = bd.image ? '<img class="cte__board-img" src="' + bd.image + '" alt="" loading="lazy">' : '<span class="cte__board-img"></span>';
      const priceLabel = d.lunchIncluded ? 'Included' : money(bd.price || 0, d.moneyFmt);
      row.innerHTML = img + '<span class="cte__board-info"><span class="cte__board-name">' + bd.name + '</span>' + (bd.desc ? '<span class="cte__board-desc">' + bd.desc + '</span>' : '') + '<span class="cte__board-foot"><span class="cte__board-price">' + priceLabel + '</span></span></span>';
      const st = document.createElement('div'); st.className = 'cte__stepper';
      st.innerHTML = '<button type="button" class="cte__pm" data-b="dec" aria-label="Remove ' + bd.name + '">&minus;</button><output class="cte__people">' + this.boardQty[bd.name] + '</output><button type="button" class="cte__pm" data-b="inc" aria-label="Add ' + bd.name + '">+</button>';
      const out = st.querySelector('output');
      st.querySelector('[data-b="dec"]').addEventListener('click', () => { if (this.boardQty[bd.name] > 0) { this.boardQty[bd.name]--; out.textContent = this.boardQty[bd.name]; this.afterBoardChange(); } });
      st.querySelector('[data-b="inc"]').addEventListener('click', () => { if (d.lunchIncluded && this.totalBoards() >= this.people) return; this.boardQty[bd.name]++; out.textContent = this.boardQty[bd.name]; this.afterBoardChange(); });
      row.querySelector('.cte__board-foot').appendChild(st); this.elBoards.appendChild(row);
    });
    this.updateLunchSub();
  }

  // A paid board changes the order total, so refresh it; included boards don't.
  afterBoardChange() {
    this.updateLunchSub();
    if (!this.data.lunchIncluded && this.elTotal) {
      this.elTotal.textContent = money((this.data.pp || 0) * this.people + this.boardsTotal(), this.data.moneyFmt);
    }
  }

  updateLunchSub() {
    if (!this.elLunchSub) return;
    const d = this.data;
    if (d.lunchIncluded) {
      this.elLunchSub.textContent = 'Lunch is included — choose up to ' + this.people + ' board' + (this.people === 1 ? '' : 's') + ' (' + this.totalBoards() + ' chosen).';
    } else {
      const t = this.boardsTotal();
      this.elLunchSub.textContent = t > 0 ? 'Lunch add-ons: ' + money(t, d.moneyFmt) : 'Add optional lunch boards.';
    }
  }

  update() {
    const ready = !!(this.selDate && this.selTime);
    this.elGuestWrap.hidden = !ready;
    const cap = this.cap();
    if (this.elMaxHint) {
      this.elMaxHint.hidden = !(ready && this.people >= cap);
      if (ready) this.elMaxHint.textContent = 'Only ' + cap + ' space' + (cap === 1 ? '' : 's') + ' left on this slot.';
    }
    this.renderLunch();
    const total = (this.data.pp || 0) * this.people + this.boardsTotal();
    if (this.elTotal) this.elTotal.textContent = money(ready ? total : (this.data.pp || 0), this.data.moneyFmt);
    this.elSave.disabled = !ready;
  }

  async save() {
    const d = this.data;
    if (!this.selDate || !this.selTime) return;
    const slotId = this.slotMap[this.selTime];
    if (!slotId) return;
    const newDate = fmtDate(this.selDate), newTime = fmtTime(this.selTime);

    const when = newDate + ' · ' + newTime;
    const props = { Date: newDate, Time: newTime, Guests: String(this.people) };

    // Included lunch -> a single (free) property on the tour line.
    if (d.hasLunch && d.lunchIncluded) {
      const chosen = (d.boards || []).filter((b) => this.boardQty[b.name] > 0).map((b) => this.boardQty[b.name] + '× ' + b.name);
      if (chosen.length) props['Lunch (included)'] = chosen.join(', ');
    }

    // Paid add-on lunch -> separate (charged) board lines, tied to the new tour
    // line by a fresh shared token so they stay nested and editable together.
    const addonItems = [];
    if (d.hasLunch && !d.lunchIncluded) {
      const picked = (d.boards || []).filter((b) => this.boardQty[b.name] > 0);
      if (picked.length) {
        const token = 'bk_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        props['_booking'] = token;
        picked.forEach((b) => addonItems.push({ id: b.id, quantity: this.boardQty[b.name], properties: { 'For tour': when, '_booking': token } }));
      }
    }

    if (newDate === d.currentDate && newTime === d.currentTime && this.people === d.guests && this.lunchUnchanged()) { this.close(); return; }

    this.elSave.disabled = true;
    this.elSave.textContent = 'Updating…';
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el instanceof HTMLElement && el.dataset.sectionId).filter(Boolean))];
    const sections = sectionIds.join(',');

    try {
      // 1) Add the new tour line plus any paid add-on boards in one request.
      const items = [{ id: slotId, quantity: this.people, properties: props }, ...addonItems];
      await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) })).then((r) => r.text());

      // 2) Remove the old tour line and its old add-on boards in one request.
      const updates = { [d.key]: 0 };
      (d.addons || []).forEach((a) => { if (a.key) updates[a.key] = 0; });
      const changeBody = JSON.stringify({ updates, sections, sections_url: window.location.pathname });
      const changeRes = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: changeBody }));
      const changeData = JSON.parse(await changeRes.text());

      let cart = undefined;
      try { cart = await (await fetch(Theme.routes.cart_url + '.js')).json(); } catch (_) {}
      // Pass the new absolute cart count so the header cart-icon updates its
      // badge. Without it the icon reads `itemCount ?? 0` and hides the number.
      const itemCount = typeof cart?.item_count === 'number' ? cart.item_count : changeData?.item_count;
      document.dispatchEvent(new CartAddEvent(cart, 'cart-tour-edit', { source: 'cart-tour-edit', itemCount, sections: changeData.sections }));
      this.close();
    } catch (_) {
      this.elSave.disabled = false;
    } finally {
      this.elSave.textContent = 'Update booking';
    }
  }

  // True when the lunch selection matches what's already in the cart, so a save
  // with no date/time/guest change can skip touching the cart entirely.
  lunchUnchanged() {
    const d = this.data;
    if (d.lunchIncluded) {
      const chosen = (d.boards || []).filter((b) => this.boardQty[b.name] > 0).map((b) => this.boardQty[b.name] + '× ' + b.name).join(', ');
      return chosen === (d.lunchProp || '');
    }
    const orig = {};
    (d.addons || []).forEach((a) => { orig[a.name] = a.qty; });
    const names = new Set([...Object.keys(orig), ...(d.boards || []).map((b) => b.name)]);
    for (const n of names) { if ((this.boardQty[n] || 0) !== (orig[n] || 0)) return false; }
    return true;
  }

  close() {
    this.panel.classList.remove('is-open');
    this.panel.style.transform = '';
  }
}

let controller = null;
function ensure() {
  if (controller) return controller;
  const panel = document.querySelector('[data-cte-panel]');
  if (!panel) return null;
  controller = new CartTourEdit(panel);
  return controller;
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-bc-edit-line]') : null;
  if (!btn) return;
  e.preventDefault();
  const c = ensure();
  if (!c) return;
  const line = btn.getAttribute('data-bc-edit-line');
  const script = document.querySelector('script[data-bc-tour-line="' + line + '"]');
  if (!script) return;
  try { c.open(JSON.parse(script.textContent)); } catch (_) {}
});
